// Per-incident structured report → premium branded PDF (client-side, jsPDF).
//
// Companion to the server route POST /api/incidents/:id/report. The
// server returns a JSON object whose keys exactly match a fixed
// section list. This module renders that JSON into a polished
// Kloudynet branded deliverable that an analyst can hand to a
// customer or attach to a ticket.
//
// Design pillars (vs. v1, which felt like a memo dump):
//   - A brand cover header (re-uses the standard reports' chrome) with
//     an "Incident Overview" hero card immediately below: severity pill,
//     status pill, incident-type chip, and a 4-up metadata grid.
//   - Sections are numbered ("01 / Executive Summary"), use the brand
//     section banner, and carry a soft accent rule under the banner.
//   - Section bodies use sub-heading detection (lines ending in ":" get
//     bolded), proper bulleted lists with hanging indent, paragraph
//     spacing, and a soft hairline divider after each section.
//   - "Human Review Required" / "Open Questions" / "Recommendations"
//     get rendered as a tinted callout block instead of plain text so
//     they stand out at a glance.
//   - Page footer carries Confidential + page x/y + an "AI-assisted"
//     disclaimer so the deliverable is unambiguous about its origin.
import {
  COLORS,
  CONTENT_W,
  MARGIN_BOTTOM,
  MARGIN_X,
  PAGE_H,
  PAGE_W,
  createReportPdf,
  drawCallout,
  drawClosingFooter,
  ensureSpace,
  finalizeFooters,
  fmtGeneratedAt,
  preloadPdfLogo,
  savePdf,
  tzAbbr,
  type DocCtx,
} from './pdfTemplate';
import { getTz } from './globalTz';

export interface IncidentReportPayload {
  ok: boolean;
  outputType: string;
  incidentType: string;
  incidentId: string;
  /** Sentinel-assigned short number (e.g. "12345"). May equal incidentId
   *  on customers where the snapshot doesn't expose it separately. */
  incidentNumber?: string | null;
  /** Title resolved server-side from the live snapshot. */
  incidentTitle?: string | null;
  customerId: string;
  customerName?: string;
  generatedAt: string;
  promptVersion: string;
  aiModel: string;
  /** Section keys, in the order they should appear. */
  sections: string[];
  /** Section-key → plain-text body. */
  report: Record<string, string>;
  /** Defanged IOC reputation appendix (synced with SOC L1 Auto-Investigation). */
  iocReputation?: Array<{
    defanged: string;
    type: string;
    overallVerdict: string;
    providerCount: number;
    maliciousCount: number;
    suspiciousCount: number;
    providers: Array<{ provider: string; verdict: string; summary: string }>;
  }>;
}

export interface ExportIncidentReportOpts {
  customerName: string;
  /** Optional incident title for the cover hero card. */
  incidentTitle?: string;
  /** Optional incident severity (Critical/High/Medium/Low/Informational). */
  incidentSeverity?: string;
  /** Optional incident status (New/Active/Closed/etc.). */
  incidentStatus?: string;
  /** Optional ISO created time for the metadata grid. */
  incidentCreatedAt?: string;
}

export interface ExportResult { ok: boolean; fileName?: string; error?: string }

type RGB = [number, number, number];

// ── Severity / status helpers ─────────────────────────────────────────────
function severityColors(sev: string | undefined): { bg: RGB; fg: RGB; label: string } {
  const s = (sev ?? '').toLowerCase();
  if (s.includes('critical')) return { bg: COLORS.danger, fg: COLORS.white, label: 'CRITICAL' };
  if (s.includes('high')) return { bg: COLORS.danger, fg: COLORS.white, label: 'HIGH' };
  if (s.includes('medium')) return { bg: COLORS.warning, fg: COLORS.white, label: 'MEDIUM' };
  if (s.includes('low')) return { bg: COLORS.yellow, fg: COLORS.ink, label: 'LOW' };
  if (s.includes('info')) return { bg: COLORS.info, fg: COLORS.white, label: 'INFORMATIONAL' };
  return { bg: COLORS.riskMinimalBg, fg: COLORS.riskMinimalFg, label: (sev || 'UNKNOWN').toUpperCase() };
}

function statusColors(status: string | undefined): { bg: RGB; fg: RGB; label: string } {
  const s = (status ?? '').toLowerCase();
  if (s === 'active' || s.includes('progress')) return { bg: COLORS.warning, fg: COLORS.white, label: (status || 'ACTIVE').toUpperCase() };
  if (s === 'new') return { bg: COLORS.statusBlue, fg: COLORS.white, label: 'NEW' };
  if (s === 'closed' || s === 'resolved') return { bg: COLORS.success, fg: COLORS.white, label: (status || 'CLOSED').toUpperCase() };
  return { bg: COLORS.riskMinimalBg, fg: COLORS.riskMinimalFg, label: (status || 'UNKNOWN').toUpperCase() };
}

function fmtDateShort(iso: string | undefined): string {
  if (!iso) return 'N/A';
  const tz = getTz();
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz,
    }).format(d) + ' ' + tzAbbr(tz);
  } catch {
    return iso;
  }
}

// ── Pill primitive (used for severity / status / chip in the hero card) ──
function drawPill(
  pdf: DocCtx['pdf'],
  x: number, y: number,
  label: string,
  bg: RGB, fg: RGB,
  opts: { fontSize?: number; padX?: number; height?: number; bold?: boolean } = {},
): number {
  const fontSize = opts.fontSize ?? 8;
  const padX = opts.padX ?? 4;
  const h = opts.height ?? 6.2;
  pdf.setFont('helvetica', opts.bold === false ? 'normal' : 'bold');
  pdf.setFontSize(fontSize);
  const textW = pdf.getStringUnitWidth(label) * fontSize / pdf.internal.scaleFactor;
  const w = textW + padX * 2;
  pdf.setFillColor(...bg);
  pdf.roundedRect(x, y, w, h, h / 2, h / 2, 'F');
  pdf.setTextColor(...fg);
  pdf.text(label, x + w / 2, y + h / 2 + 0.4, { align: 'center', baseline: 'middle' });
  return w;
}

// ── Hero overview card ────────────────────────────────────────────────────
function drawIncidentOverview(
  ctx: DocCtx,
  payload: IncidentReportPayload,
  opts: ExportIncidentReportOpts,
): void {
  const { pdf } = ctx;
  ensureSpace(ctx, 64);
  const x = MARGIN_X;
  const y = ctx.y;
  const w = CONTENT_W;

  // Soft drop shadow
  pdf.setFillColor(220, 225, 232);
  pdf.roundedRect(x + 0.5, y + 0.7, w, 56, 3, 3, 'F');
  // Card body
  pdf.setFillColor(...COLORS.white);
  pdf.roundedRect(x, y, w, 56, 3, 3, 'F');
  pdf.setDrawColor(...COLORS.border);
  pdf.setLineWidth(0.2);
  pdf.roundedRect(x, y, w, 56, 3, 3, 'S');
  // Left accent bar
  pdf.setFillColor(...COLORS.primary);
  pdf.rect(x, y, 2.4, 56, 'F');

  // Eyebrow
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(7.5);
  pdf.setTextColor(...COLORS.muted);
  pdf.text('INCIDENT REPORT', x + 7, y + 7);

  // Big title (incident title, falls back to ID)
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(15);
  pdf.setTextColor(...COLORS.primaryDark);
  const titleStr = opts.incidentTitle ?? `Incident ${payload.incidentId}`;
  const titleLines: string[] = pdf.splitTextToSize(titleStr, w - 14);
  let ty = y + 13.5;
  for (const ln of titleLines.slice(0, 2)) {
    pdf.text(String(ln), x + 7, ty);
    ty += 6;
  }

  // Pill row: severity / status / incident type chip
  let px = x + 7;
  const py = ty + 1;
  const sev = severityColors(opts.incidentSeverity);
  const stat = statusColors(opts.incidentStatus);
  px += drawPill(pdf, px, py, sev.label, sev.bg, sev.fg, { fontSize: 7.5, padX: 4, height: 5.6 }) + 3;
  px += drawPill(pdf, px, py, stat.label, stat.bg, stat.fg, { fontSize: 7.5, padX: 4, height: 5.6 }) + 3;
  // Incident-type chip (light, with primary outline)
  const chipLabel = (payload.incidentType || 'generic').toUpperCase();
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(7.5);
  const chipW = pdf.getStringUnitWidth(chipLabel) * 7.5 / pdf.internal.scaleFactor + 8;
  pdf.setFillColor(239, 246, 255);
  pdf.roundedRect(px, py, chipW, 5.6, 2.8, 2.8, 'F');
  pdf.setDrawColor(...COLORS.primary);
  pdf.setLineWidth(0.2);
  pdf.roundedRect(px, py, chipW, 5.6, 2.8, 2.8, 'S');
  pdf.setTextColor(...COLORS.primary);
  pdf.text(chipLabel, px + chipW / 2, py + 2.8 + 0.4, { align: 'center', baseline: 'middle' });

  // Hairline divider
  const dividerY = py + 9;
  pdf.setDrawColor(...COLORS.rule);
  pdf.setLineWidth(0.2);
  pdf.line(x + 7, dividerY, x + w - 7, dividerY);

  // 4-up metadata grid
  const cols: Array<[string, string]> = [
    ['INCIDENT ID', payload.incidentId],
    ['CUSTOMER', payload.customerName ?? payload.customerId],
    ['CREATED', fmtDateShort(opts.incidentCreatedAt)],
    ['REPORT TYPE', payload.outputType],
  ];
  const colW = (w - 14) / cols.length;
  const gridY = dividerY + 4;
  cols.forEach(([k, v], i) => {
    const cx = x + 7 + i * colW;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(6.8);
    pdf.setTextColor(...COLORS.muted);
    pdf.text(k, cx, gridY);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9.5);
    pdf.setTextColor(...COLORS.primaryDark);
    const valLines: string[] = pdf.splitTextToSize(String(v ?? 'N/A'), colW - 4);
    let vy = gridY + 4.2;
    for (const ln of valLines.slice(0, 2)) {
      pdf.text(String(ln), cx, vy);
      vy += 4;
    }
  });

  ctx.y = y + 56 + 6;
}

// ── Section banner with number + accent rule ──────────────────────────────
function drawNumberedSection(ctx: DocCtx, n: number, label: string, accent: RGB): void {
  ensureSpace(ctx, 18);
  const { pdf } = ctx;
  const y = ctx.y;
  const h = 11;

  // Background bar - soft tinted gradient via 2 tones (left=accent, right=fade)
  pdf.setFillColor(...accent);
  pdf.rect(MARGIN_X, y, CONTENT_W, h, 'F');

  // Number badge (white pill, accent text)
  const badgeW = 11;
  const badgeH = 7;
  pdf.setFillColor(...COLORS.white);
  pdf.roundedRect(MARGIN_X + 3, y + (h - badgeH) / 2, badgeW, badgeH, 1.2, 1.2, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8.5);
  pdf.setTextColor(...accent);
  pdf.text(String(n).padStart(2, '0'), MARGIN_X + 3 + badgeW / 2, y + h / 2 + 0.3, { align: 'center', baseline: 'middle' });

  // Section label
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(...COLORS.white);
  pdf.text(label.toUpperCase(), MARGIN_X + 3 + badgeW + 4, y + h / 2 + 0.5, { baseline: 'middle' });

  ctx.y = y + h;

  // Accent hairline immediately below banner
  pdf.setDrawColor(...accent);
  pdf.setLineWidth(0.8);
  pdf.line(MARGIN_X, ctx.y + 0.6, MARGIN_X + 28, ctx.y + 0.6);
  ctx.y += 5;
}

// ── Body renderer with sub-heading + bullet awareness ─────────────────────
function renderSectionBody(ctx: DocCtx, body: string): void {
  const { pdf } = ctx;
  const text = (body ?? '').trim();
  if (!text) {
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(9.5);
    pdf.setTextColor(...COLORS.muted);
    pdf.text('No content returned for this section.', MARGIN_X, ctx.y + 4);
    ctx.y += 8;
    return;
  }

  const bodyFontSize = 9.8;
  const lineH = 4.6;
  const paragraphs = text.split(/\n+/);

  for (const rawPara of paragraphs) {
    const para = rawPara.replace(/\s+$/, '');
    if (!para) {
      ctx.y += lineH * 0.5;
      continue;
    }

    // Sub-heading detection - intentionally conservative so we don't
    // bold normal sentences that happen to end with ":". Two patterns
    // qualify as a heading:
    //   (a) Markdown-style **Bold** opener.
    //   (b) An ALL-CAPS line ending in ":" with no body text after the
    //       colon (e.g. "RECOMMENDED ACTIONS:"). Title-case "Foo:" lines
    //       are NOT treated as headings - they read as lead-ins.
    const boldWrapped = /^\*\*(.+)\*\*:?\s*(.*)$/.exec(para);
    const headingMatch = /^([A-Z][A-Z0-9 /&\-()]{2,50}):\s*$/.exec(para);
    if (boldWrapped) {
      ensureSpace(ctx, lineH + 3);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(bodyFontSize + 0.6);
      pdf.setTextColor(...COLORS.primaryDark);
      pdf.text(boldWrapped[1] ?? '', MARGIN_X, ctx.y);
      ctx.y += lineH + 0.5;
      const rest = (boldWrapped[2] ?? '').trim();
      if (rest) renderParagraphPlain(ctx, rest, lineH, bodyFontSize);
      ctx.y += 1;
      continue;
    }
    if (headingMatch) {
      ensureSpace(ctx, lineH + 3);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(bodyFontSize + 0.4);
      pdf.setTextColor(...COLORS.primaryDark);
      pdf.text(headingMatch[1] ?? '', MARGIN_X, ctx.y);
      ctx.y += lineH + 0.8;
      continue;
    }

    // Bulleted list item - hanging indent.
    const bulletMatch = /^\s*[-•*]\s+(.*)$/.exec(para);
    if (bulletMatch) {
      renderBullet(ctx, bulletMatch[1] ?? '', lineH, bodyFontSize);
      continue;
    }

    // Numbered list item ("1. foo")
    const numMatch = /^\s*(\d+\.)\s+(.*)$/.exec(para);
    if (numMatch) {
      renderNumberedItem(ctx, numMatch[1] ?? '', numMatch[2] ?? '', lineH, bodyFontSize);
      continue;
    }

    renderParagraphPlain(ctx, para, lineH, bodyFontSize);
    ctx.y += 1.5;
  }

  ctx.y += 2;
}

function renderParagraphPlain(ctx: DocCtx, text: string, lineH: number, fontSize: number): void {
  const { pdf } = ctx;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(fontSize);
  pdf.setTextColor(...COLORS.ink);
  const lines: string[] = pdf.splitTextToSize(text, CONTENT_W);
  for (const line of lines) {
    if (ctx.y + lineH > PAGE_H - MARGIN_BOTTOM) {
      pdf.addPage();
      ctx.y = 18;
    }
    pdf.text(String(line), MARGIN_X, ctx.y);
    ctx.y += lineH;
  }
}

function renderBullet(ctx: DocCtx, text: string, lineH: number, fontSize: number): void {
  const { pdf } = ctx;
  const indent = 6;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(fontSize);
  pdf.setTextColor(...COLORS.ink);
  const lines: string[] = pdf.splitTextToSize(text, CONTENT_W - indent);
  for (let i = 0; i < lines.length; i++) {
    if (ctx.y + lineH > PAGE_H - MARGIN_BOTTOM) {
      pdf.addPage();
      ctx.y = 18;
    }
    if (i === 0) {
      pdf.setFillColor(...COLORS.primary);
      pdf.circle(MARGIN_X + 1.6, ctx.y - 1.2, 0.85, 'F');
    }
    pdf.setTextColor(...COLORS.ink);
    pdf.text(String(lines[i] ?? ''), MARGIN_X + indent, ctx.y);
    ctx.y += lineH;
  }
  ctx.y += 0.6;
}

function renderNumberedItem(ctx: DocCtx, marker: string, text: string, lineH: number, fontSize: number): void {
  const { pdf } = ctx;
  const indent = 8;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(fontSize);
  pdf.setTextColor(...COLORS.primary);
  if (ctx.y + lineH > PAGE_H - MARGIN_BOTTOM) {
    pdf.addPage();
    ctx.y = 18;
  }
  pdf.text(marker, MARGIN_X + 1, ctx.y);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...COLORS.ink);
  const lines: string[] = pdf.splitTextToSize(text, CONTENT_W - indent);
  for (let i = 0; i < lines.length; i++) {
    if (i > 0 && ctx.y + lineH > PAGE_H - MARGIN_BOTTOM) {
      pdf.addPage();
      ctx.y = 18;
    }
    pdf.text(String(lines[i] ?? ''), MARGIN_X + indent, ctx.y);
    if (i < lines.length - 1) ctx.y += lineH;
  }
  ctx.y += lineH + 0.6;
}

function drawSoftDivider(ctx: DocCtx): void {
  const { pdf } = ctx;
  ensureSpace(ctx, 4);
  pdf.setDrawColor(...COLORS.rule);
  pdf.setLineWidth(0.2);
  pdf.line(MARGIN_X + 30, ctx.y + 1, MARGIN_X + CONTENT_W - 30, ctx.y + 1);
  ctx.y += 4;
}

// ── Filename helper ──────────────────────────────────────────────────────
/**
 * Conservative file-system slug. Allows ASCII letters, digits, dash,
 * underscore, and SPACE so the user-requested "<Title> - <Number>"
 * format reads naturally on disk. Other punctuation (slashes, colons,
 * quotes, accented characters) collapses to a single dash.
 */
function safeFileSlug(s: string): string {
  return s
    .replace(/[^a-zA-Z0-9 _-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/\s{2,}/g, ' ')
    .replace(/^[-\s]+|[-\s]+$/g, '')
    .slice(0, 80);
}

// ── Entry point ──────────────────────────────────────────────────────────
export async function exportIncidentReportPdf(
  payload: IncidentReportPayload,
  opts: ExportIncidentReportOpts,
): Promise<ExportResult> {
  try {
    await preloadPdfLogo();
    const ctx = createReportPdf({
      title: payload.outputType,
      customerName: opts.customerName,
      periodLabel: opts.incidentTitle
        ? `Incident ${payload.incidentId} · ${opts.incidentTitle}`
        : `Incident ${payload.incidentId}`,
      generatedLabel: fmtGeneratedAt(payload.generatedAt, getTz()),
    });

    drawIncidentOverview(ctx, payload, opts);

    // ── Sections ──────────────────────────────────────────────────────
    // "Soft" sections that read more like an executive narrative get a
    // primary-toned banner. Sections that flag analyst-required action
    // get the warning tone and are rendered as a callout block so they
    // can never be missed.
    const calloutSectionKeys = new Set([
      'open questions',
      'recommendations',
      'recommended actions',
      'next steps',
    ]);

    // "Human Review Required" is an internal QA gate the prompt always
    // produces, not client-facing content - it never belongs in the
    // exported deliverable, so it's dropped here rather than left for an
    // analyst to manually strip out of the PDF before sharing it.
    const renderedSections = payload.sections.filter((key) => !key.toLowerCase().includes('human review'));

    renderedSections.forEach((key, idx) => {
      const body = (payload.report[key] ?? '').trim();
      const lk = key.toLowerCase();
      const accent: RGB = COLORS.primaryDark;

      drawNumberedSection(ctx, idx + 1, key, accent);

      if (calloutSectionKeys.has(lk) && body) {
        // Render as a tinted callout block - but drawCallout draws as a
        // single unsplittable rectangle, so guard against oversized
        // bodies that would clip a fresh page. Estimated height = 9
        // (chrome) + lines * 4. If it can't fit in a single A4 page,
        // fall back to the regular paragraph renderer.
        const usablePageH = PAGE_H - 18 /* top */ - MARGIN_BOTTOM;
        const wrapped: string[] = ctx.pdf.splitTextToSize(body, CONTENT_W - 10);
        const estH = 9 + wrapped.length * 4;
        if (estH > usablePageH - 6) {
          renderSectionBody(ctx, body);
        } else {
          const tone = lk.includes('recommend') || lk.includes('next') ? 'success' : 'info';
          drawCallout(ctx, {
            title: key,
            body: body.replace(/^\s*[-•*]\s+/gm, '• '),
            tone,
          });
        }
      } else {
        renderSectionBody(ctx, body);
      }

      if (idx < renderedSections.length - 1) drawSoftDivider(ctx);
    });

    // ── Closing brand banner + page footers ──────────────────
    drawClosingFooter(ctx);
    finalizeFooters(ctx);

    // Filename = "<Incident Title> - <Incident Number>" (slugified for
    // file-system safety). Falls back to title-only or number-only when
    // either field is missing, and finally to a generic incident slug.
    const titleSrc = (opts.incidentTitle ?? payload.incidentTitle ?? '').trim();
    const numberSrc = (payload.incidentNumber ?? payload.incidentId ?? '').toString().trim();
    const titleSlug = safeFileSlug(titleSrc);
    const numberSlug = safeFileSlug(numberSrc);
    const fileBase =
      titleSlug && numberSlug ? `${titleSlug} - ${numberSlug}`
      : titleSlug ? titleSlug
      : numberSlug ? `incident-${numberSlug}`
      : 'incident-report';
    const fileName = savePdf(ctx, fileBase);
    return { ok: true, fileName };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}
